import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import { createCostCenter, fetchCostCenters, updateCostCenter } from "../../../api/accounting";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { useLatestRef } from "./accountingUtils";

const CostCentersPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: "", name: "" });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(() => fetchCostCenters(branchId || undefined).then(setRows), {
      errorMessage: "Failed to load cost centers.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load cost centers.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const setActive = useCallback(
    (row, isActive) => {
      confirm({
        title: isActive ? `Reactivate ${row.code}?` : `Deactivate ${row.code}?`,
        confirmLabel: isActive ? "Reactivate" : "Deactivate",
        tone: isActive ? "primary" : "danger",
        successMessage: isActive ? "Cost center reactivated." : "Cost center deactivated.",
        onConfirm: async () => {
          await updateCostCenter(row.id, { is_active: isActive });
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Code", selector: (r) => r.code, sortable: true, width: "120px" },
      { name: "Name", selector: (r) => r.name, sortable: true, grow: 2 },
      { name: "Branch", selector: (r) => r.branch_name || "All", width: "140px" },
      {
        name: "Status",
        cell: (r) => (
          <Badge color={r.is_active ? "success" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge>
        ),
        width: "100px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              {
                icon: r.is_active ? "clear" : "plus",
                label: r.is_active ? "Deactivate cost center" : "Activate cost center",
                onClick: () => setActive(r, !r.is_active),
                variant: r.is_active ? "danger" : "primary",
              },
            ]}
          />
        ),
        width: "56px",
        ignoreRowClick: true,
      },
    ],
    [setActive]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () => createCostCenter({ branch_id: branchId || null, code: form.code, name: form.name }),
        { successMessage: "Cost center created." }
      );
      setModalOpen(false);
      setForm({ code: "", name: "" });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Cost Centers" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Cost centers</h5>
            <div className="d-flex gap-2">
              <Input type="select" bsSize="sm" style={{ width: "180px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>New cost center</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New cost center</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Code</Label>
                <Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label>Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>{saving ? "Creating…" : "Create"}</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default CostCentersPage;
